import React, { useState } from 'react';
import { Box, Typography, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Avatar, Button, TableSortLabel, styled, Grid2, Grid, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Vehicle } from '../../state/types';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Vehicle360Modal from './Vehicle360Modal';
import RoleBasedAccess from '../../services/RoleBasedAccess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFilesAsync } from '../../services/FileDownloader';
import { useVehiclesContext } from '../../context/VehiclesContext';
import { useVehicleTableLogic } from '../../hooks/vehicles/useVehicleTableLogic';

const VehicleDesktopTable: React.FC = () => {
  const navigate = useNavigate();
  const { vehicles, sortDirection, setSortDirection } = useVehiclesContext();
  const {
    openModal,
    modalVehicle,
    thumbnailErrors,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    handleSelectAllClick,
    isSelected,
    isAllSelected,
    isIndeterminate,
  } = useVehicleTableLogic();

  const getSyndicationStep = (vehicle: Vehicle) => {
    if (!vehicle || !vehicle.syndicationSteps || !vehicle.syndicationCurrentStep) return ''

    if (vehicle.syndicationCurrentStep === "COMPLETE") return vehicle?.syndicationSteps[0]

    return vehicle.syndicationCurrentStep
  }

  const StatusTag = styled('span')(
    ({
      theme,
      status,
    }: {
      theme?: any;
      status: 'COMPLETE' | 'ERROR' | 'PENDING' | 'NOT STARTED' | string;
    }) => {
      let bgColor = theme.palette.grey[500]; // Default for NOT STARTED

      switch (status) {
        case 'COMPLETE':
          bgColor = theme.palette.success.main;
          break;
        case 'ERROR':
          bgColor = theme.palette.error.main;
          break;
        case 'PENDING':
          bgColor = theme.palette.warning.main;
          break;
        case 'NOT STARTED':
          bgColor = theme.palette.grey[400];
          break;
        default:
          bgColor = theme.palette.grey[500];
      }

      return {
        backgroundColor: bgColor,
        color: theme.palette.background.paper,
        borderRadius: '3px',
        padding: '4px 8px',
        display: 'inline-block',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      };
    }
  );

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                <TableCell padding="checkbox">
                  <Checkbox
                    onClick={handleSelectAllClick}
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: "darkred",
                      },
                    }}
                  />
                </TableCell>
              </RoleBasedAccess>
              <TableCell>ID</TableCell>
              <TableCell>Image Counts</TableCell>
              <TableCell>Processing</TableCell>
              <TableCell>Syndication</TableCell>
              <TableCell>
                <TableSortLabel
                  active
                  direction={sortDirection}
                  onClick={() =>
                    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
                  }
                >
                  Post Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Config</TableCell>
              <TableCell>360</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.map((vehicle) => (
              <TableRow
                key={vehicle.assetId}
                hover
              >
                <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={(e) => handleCheckboxClick(e, vehicle)}
                      checked={isSelected(vehicle)}
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: "darkred",
                        },
                      }}
                    />
                  </TableCell>
                </RoleBasedAccess>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <Box display="flex" alignItems="center" flexDirection="column" justifyContent="center" gap={0.25}>
                      <Link
                        component={RouterLink}
                        to={`/vehicle/${vehicle.clientId}/${vehicle.assetId}`}
                        underline="hover"
                        sx={{
                          color: "inherit",
                          fontWeight: 500,
                          textAlign: "center",
                          marginBottom: 0.5,
                          wordBreak: "break-word",
                          cursor: "pointer",
                          fontSize: "1rem",
                        }}
                      >
                        {vehicle.vin}
                      </Link>

                      {vehicle.archived ? (
                        <Box
                          sx={{
                            height: 120,
                            width: 200,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            Archived Vehicle
                          </Typography>
                        </Box>
                      ) : vehicle.thumbnail && !thumbnailErrors[vehicle.assetId] ? (
                        <Box
                          onClick={() => navigate(`/vehicle/${vehicle.clientId}/${vehicle.assetId}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover img': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Avatar
                            variant="square"
                            src={vehicle.thumbnail}
                            onError={() => handleThumbnailError(vehicle.assetId)}
                            sx={{ height: 120, width: 200, borderRadius: 3 }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            height: 100,
                            width: 180,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            No Thumbnail
                          </Typography>
                        </Box>
                      )}
                    </Box>


                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box display="flex" flexDirection="column" justifyContent="space-between" height="140px">
                        <Tooltip title="Copy VIN">
                          <IconButton
                            component="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigator.clipboard.writeText(vehicle.vin);
                            }}
                            sx={{
                              padding: 0.25,            // make button tighter
                              color: (theme) => theme.palette.grey[400], // light grey
                              '&:hover': {
                                color: (theme) => theme.palette.grey[600], // slightly darker on hover
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} /> {/* super small */}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open in New Tab">
                          <IconButton
                            component="a"
                            href={`/vehicle/${vehicle.clientId}/${vehicle.assetId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} // ✅ Prevents row click but allows new tab
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <OpenInNewIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Images">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFilesAsync(vehicle);
                            }}
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <DownloadIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ px: 0 }}>
                  {[
                    { key: "imageExterior", label: "Exterior", images: vehicle.imageExterior || [] },
                    { key: "imageCrop", label: "Cropped", images: vehicle.imageCrop || [] },
                    { key: "imageFeatured", label: "Featured", images: vehicle.imageFeatured || [] },
                    { key: "imageManual", label: "Manual", images: vehicle.imageManual || [] },
                    { key: "imagePano", label: "Panoramic", images: vehicle.imagePano || [] },
                    { key: "imageUnderCarriage", label: "UC", images: vehicle.imageUndercarriage || [] },
                    { key: "imageBgr", label: "BGR", images: vehicle.imageBgr || [] },
                  ].map(({ key, label, images }) => (
                    <Grid2 container key={key} sx={{ gap: 0.5, marginRight: 6 }}>
                      <Grid2 size={{ xs: 12, sm: 10 }} sx={{ alignItems: 'end', p: 0, width: 'fit-content' }}>
                        <Typography sx={{ textAlign: 'end' }}>
                          {`${label}:`}
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 1 }}>
                        <Typography>
                          {images.length}
                        </Typography>
                      </Grid2>
                    </Grid2>
                  ))}
                </TableCell>

                <TableCell>
                  <StatusTag
                    status={vehicle.processingStatus || ''}
                    title={vehicle.processingError || undefined}
                  >
                    {vehicle.processingCurrentStep}
                  </StatusTag>
                </TableCell>
                <TableCell>
                  <StatusTag
                    status={vehicle.syndicationStatus || ''}
                    title={vehicle.syndicationError || undefined}
                  >
                    {getSyndicationStep(vehicle)}
                  </StatusTag>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(+vehicle.created_at * 1000).toLocaleString([], {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>{vehicle.config}</TableCell>
                <TableCell>
                  {vehicle.imagePano?.length ? (
                    <SpinningButton onClick={(e: React.MouseEvent) => handle360Click(e, vehicle.clientId, vehicle.assetId)}>
                      360
                      <SyncTwoToneIcon sx={{ fontSize: '18px', marginLeft: '5px' }} />
                    </SpinningButton>
                  ) : (
                    <Typography>No Panos</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Vehicle 360 Modal */}
      <Vehicle360Modal
        open={openModal}
        handleClose={handleCloseModal}
        clientId={modalVehicle?.clientId || null}
        assetId={modalVehicle?.assetId || null}
      />
    </>
  );
};

export default VehicleDesktopTable;
